import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { Check, AlertCircle, X } from 'lucide-react';

type ToastType = 'info' | 'success' | 'error';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

let toastId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Toast container — fixed position, top-right */}
      <div className="fixed top-6 right-6 z-[100] flex flex-col gap-3 pointer-events-none" aria-live="polite" aria-atomic="true">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="pointer-events-auto toast-enter flex items-start gap-3 min-w-[320px] max-w-[400px] p-4 rounded-xl border border-dash-border bg-dash-surface shadow-lg"
            style={{ borderLeftWidth: '3px', borderLeftColor: toast.type === 'success' ? '#34D399' : toast.type === 'error' ? '#F87171' : '#E5AC41' }}
          >
            {toast.type === 'success' ? (
              <Check className="w-5 h-5 text-dash-success mt-0.5 shrink-0" />
            ) : toast.type === 'error' ? (
              <AlertCircle className="w-5 h-5 text-dash-error mt-0.5 shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-dash-accent mt-0.5 shrink-0" />
            )}
            <p className="text-sm font-medium text-dash-text flex-1">{toast.message}</p>
            <button
              onClick={() => removeToast(toast.id)}
              className="text-dash-text-tertiary hover:text-dash-text transition-colors"
              aria-label="Dismiss notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextType {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
